import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WebsocketService implements OnDestroy {
  private socket$!: WebSocketSubject<any>;
  private readonly messagesSubject = new Subject<any>();
  public messages$: Observable<any> = this.messagesSubject.asObservable();

  private readonly WS_URL = environment.socket_url;

  private readonly idleTimeout = 60000; // 1 minute idle
  private idleTimerSub?: Subscription;

  private manualClose = false; // flag to indicate manual/browser close

  constructor() {
    this.connect();

    // Close socket if user closes tab/browser
    globalThis.window.addEventListener('beforeunload', () => {
      this.manualClose = true;
      this.closeConnection();
    });
  }

  private connect(): void {
    this.socket$ = webSocket({
      url: this.WS_URL,
      openObserver: {
        next: () => {
          
          this.startIdleTimer();
        },
      },
      closeObserver: {
        next: (event) => {
          console.warn('[WebSocket] Connection closed', event);
          this.clearIdleTimer();

          // Only auto-reconnect if it wasn't a manual/browser close
          if (!this.manualClose) {
            setTimeout(() => this.connect(), 15000);
          }

          // Reset manualClose flag for future reconnects
          this.manualClose = false;
        }
      }
    });

    this.socket$.subscribe({
      next: (msg) => {
        
        this.messagesSubject.next(msg);
        this.resetIdleTimer();
      },
      error: (err) => {
        console.error('[WebSocket] Error:', err);
        this.clearIdleTimer();
        this.socket$.complete();
      }
    });
  }

  public sendMessage(msg: any): void {
    if (this.socket$) {
      this.socket$.next(msg);
      this.resetIdleTimer();
    }
  }

  public closeConnection(): void {
    
    this.clearIdleTimer();
    if (this.socket$) {
      this.socket$.complete(); // closes the socket
    }
  }

  private startIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimerSub = timer(this.idleTimeout).subscribe(() => {
      console.warn(`[WebSocket] Idle for ${this.idleTimeout / 1000}s — closing connection`);
      this.manualClose = false; // idle close is not manual, allow reconnect
      this.closeConnection();
    });
  }

  private resetIdleTimer(): void {
    this.startIdleTimer();
  }

  private clearIdleTimer(): void {
    if (this.idleTimerSub) {
      this.idleTimerSub.unsubscribe();
      this.idleTimerSub = undefined;
    }
  }

  ngOnDestroy(): void {
    this.manualClose = true;
    this.closeConnection();
    globalThis.window.removeEventListener('beforeunload', () => this.closeConnection());
  }
}
